import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Alert, Button, Form, Input, InputNumber, Modal, Radio, Select, Card, Space, Divider } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { get_modules, get_workspaces } from '../../../redux/actions/organizationActions';
import { self_rule_create, self_rule_update } from '../../../redux/actions/selfRulesActions';

// Utils
import isEmpty from '../../../utils/isEmpty';
import { load_scope_options, rule_type_options } from '../../../utils/select_options';
import { create_select_options, create_select_options_from_array } from '../../../utils/create_select_options';

const RulesFormModal = (props) => {
	const { messageApi, isModalOpen, setIsModalOpen, orgFilters, update, ruleToUpdate, setRuleToUpdate, pageSize, skip } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { organizations, workspaces, modules } = useSelector(state => state.organization);

	const { all_self_schemas } = useSelector(state => state.self_schemas);
	const { all_self_rules, self_rule_create_loading, self_rule_update_loading, self_rules_errors } = useSelector(state => state.self_rules);

	const [form] = Form.useForm();
	const [currentIndex, setCurrentIndex] = useState(0);
	const [ruleType, setRuleType] = useState(0);
	const [loadScopeOptions, setLoadScopeOptions] = useState([]);
	const [ruleTypesOptions, setRuleTypesOptions] = useState([]);
	const [compoundConditions, setCompoundConditions] = useState([{
		field_type: 3,
		schema: '',
		values: '',
		operator: null
	}]);
	const [operator, setOperator] = useState('');

	const LOAD_SCOPES = load_scope_options();
	const RULE_TYPES = rule_type_options();

	useEffect(() => {
		setLoadScopeOptions(create_select_options_from_array(LOAD_SCOPES));
		setRuleTypesOptions(create_select_options_from_array(RULE_TYPES));
	}, []);

	useEffect(() => {
		const rulesCount = all_self_rules.count + 1;
		setCurrentIndex(rulesCount);
	}, [all_self_rules]);

	useEffect(() => {
		if (!isEmpty(ruleToUpdate)) {
			const values = {
				name: ruleToUpdate.name,
				description: ruleToUpdate.description,
				active: ruleToUpdate.active,
				index: ruleToUpdate.index,
				schema: ruleToUpdate.reference?._id?.$oid,
				type: ruleToUpdate.field_type,
				number: ruleToUpdate.number,
				action: ruleToUpdate.action,
				operator: ruleToUpdate.operator
			};

			// Handle different rule types
			if (ruleToUpdate.field_type === 3) {
				values.values = ruleToUpdate.values?.join(', ');
			} else if (ruleToUpdate.field_type === 5) {
				// Handle compound rules
				setOperator(ruleToUpdate.operator);
				if (ruleToUpdate.conditions) {
					const conditions = ruleToUpdate.conditions.map(condition => {
						if (condition.field_type === 5 && condition.operator) {
							// Handle conditions with operators (AND, OR, NOT)
							return {
								field_type: 3,
								schema: condition.conditions[0]?.schema || '',
								values: condition.conditions[0]?.values?.join(', ') || '',
								operator: condition.operator
							};
						} else if (condition.field_type === 3) {
							// Handle simple conditions
							return {
								field_type: 3,
								schema: condition.schema || '',
								values: condition.values?.join(', ') || '',
								operator: null
							};
						}
						return condition;
					});
					setCompoundConditions(conditions);
				}
			}

			form.setFieldsValue(values);
			setRuleType(ruleToUpdate.field_type);
		}
		else {
			form.resetFields();
		}
	}, [ruleToUpdate]);

	const handleCancel = () => {
		form.resetFields();
		setIsModalOpen(false);
		setRuleToUpdate({});
		setRuleType(0);
		setCompoundConditions([{
			field_type: 3,
			schema: '',
			values: '',
			operator: null
		}]);
		setOperator('');
	}

	// Functions to handle compound conditions
	const addCondition = () => {
		setCompoundConditions([...compoundConditions, {
			field_type: 3,
			schema: '',
			values: '',
			operator: null
		}]);
	};

	const removeCondition = (index) => {
		const newConditions = compoundConditions.filter((_, i) => i !== index);
		setCompoundConditions(newConditions);
	};

	const updateCondition = (index, field, value) => {
		const newConditions = [...compoundConditions];
		newConditions[index][field] = value;
		setCompoundConditions(newConditions);
	};

	const onValuesChange = (changedValues, allValues) => {
		if (changedValues.organization) {
			form.setFieldsValue({ workspace: undefined, module: undefined });

			dispatch(get_workspaces({ organization: changedValues.organization }));
			dispatch(get_modules({ organization: changedValues.organization }));
		}

		if (changedValues.workspace) {
			form.setFieldsValue({ module: undefined });
		}

		if (changedValues.type) {
			setRuleType(changedValues.type);

			if (changedValues.type === 1 || changedValues.type === 2) {
				form.setFieldsValue({ values: undefined });
			}
			else if (changedValues.type === 3) {
				form.setFieldsValue({ number: undefined });
			}
			else if (changedValues.type === 5) {
				form.setFieldsValue({ number: undefined, values: undefined });
			}
		}
	}

	const onOk = () => {
		form
			.validateFields()
			.then((values) => {
				// Handle different rule types
				if (values.type === 5) {
					// Compound rule
					const conditions = compoundConditions
						.filter(condition => condition.schema && condition.values)
						.map(condition => {
							// Split the values string by comma to create array
							const processedValues = condition.values
								.split(',')
								.map(v => v.trim())
								.filter(v => v);
								
							if (condition.operator) {
								// If the condition has an operator (AND, OR, NOT), wrap it in a compound structure
								return {
									field_type: 5,
									operator: condition.operator,
									conditions: [{
										field_type: 3,
										schema: condition.schema,
										values: processedValues
									}]
								};
							} else {
								// Simple condition without operator
								return {
									field_type: 3,
									schema: condition.schema,
									values: processedValues
								};
							}
						});

					const body = {
						organization: values.organization ?? orgFilters.organization,
						workspace: values.workspace ?? orgFilters.workspace,
						module: values.module ?? orgFilters.module,
						scope: values.scope,
						name: values.name,
						description: values.description || values.name,
						active: values.active,
						index: !update ? currentIndex : (ruleToUpdate?.index ?? values.index),
						schema: compoundConditions[0]?.schema || values.schema, // Use first condition's schema as reference
						type: 5,
						operator: values.operator || operator,
						conditions: conditions,
						action: values.action
					};

					if (!update) {
						const filters = { ...orgFilters, skip: skip, limit: pageSize };
						dispatch(self_rule_create(messageApi, body, handleCancel, filters));
					} else {
						const filters = { ...orgFilters, skip: skip, limit: pageSize };
						dispatch(self_rule_update(messageApi, ruleToUpdate?._id?.$oid, body, handleCancel, filters));
					}
				} else {
					// Simple rule
					if (values.values) {
						values.values = values.values
							.split(',')
							.map(item => item.trim())
							.filter(item => item !== '');
					} else {
						values.values = [];
					}

					const body = {
						...values,
						app: null,
						organization: values.organization ?? orgFilters.organization,
						workspace: values.workspace ?? orgFilters.workspace,
						module: values.module ?? orgFilters.module,
						index: !update ? currentIndex : (ruleToUpdate?.index ?? values.index),
						action: values.action || 'FILTER'
					};

					if (!update) {
						const filters = { ...orgFilters, skip: skip, limit: pageSize };
						dispatch(self_rule_create(messageApi, body, handleCancel, filters));
					} else {
						const filters = { ...orgFilters, skip: skip, limit: pageSize };
						dispatch(self_rule_update(messageApi, ruleToUpdate?._id?.$oid, body, handleCancel, filters));
					}
				}
			})
			.catch((info) => {
				console.error(info);
			});
	}

	return (
		<Modal
			title={!update ? t('rulesForm.create.title') : t('rulesForm.edit.title')}
			centered
			open={isModalOpen}
			onCancel={handleCancel}
			maskClosable={false}
			width={700}
			footer={[
				<Button key='back' onClick={handleCancel}>
					{t('schemasForm.cancel')}
				</Button>,
				<Button
					key='submit'
					type='primary'
					loading={!update ? self_rule_create_loading : self_rule_update_loading}
					onClick={onOk}
				>
					{!update ? t('rulesForm.create.title').split(' ')[0] : t('rulesForm.edit.title').split(' ')[0]}
				</Button>,
			]}
		>
			<Form
				form={form}
				onValuesChange={onValuesChange}
				labelCol={{ span: 5 }}
				labelWrap
			>
				{self_rules_errors.hasOwnProperty('self_rule_create') &&
					<div style={{ marginBottom: '1.5rem' }}>
						<Alert message={t('rulesForm.create.msg.error')} type='error' showIcon />
					</div>
				}

				{self_rules_errors.hasOwnProperty('self_rule_update') &&
					<div style={{ marginBottom: '1.5rem' }}>
						<Alert message={t('rulesForm.edit.msg.error')} type='error' showIcon />
					</div>
				}

				{self_rules_errors.hasOwnProperty('self_rule_update_not_modified') &&
					<div style={{ marginBottom: '1.5rem' }}>
						<Alert message={t('rulesForm.edit.msg.notModified')} type='info' showIcon />
					</div>
				}

				{!update &&
					<Fragment>
						{orgFilters.organization === '' &&
							<Form.Item
								label={t('filters.organization')}
								name='organization'
								rules={[
									{
										required: true,
										message: t('loadForm.validationMsg.organization'),
									}
								]}
							>
								<Select
									showSearch
									placeholder={t('loadForm.placeholder.organization')}
									optionFilterProp='children'
									filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
									options={create_select_options(organizations, 'name')}
								/>
							</Form.Item>
						}
						{orgFilters.workspace === '' &&
							<Form.Item
								label={t('filters.workspace')}
								name='workspace'
							>
								<Select
									showSearch
									placeholder={t('loadForm.placeholder.workspace')}
									optionFilterProp='children'
									filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
									options={create_select_options(workspaces, 'name')}
								/>
							</Form.Item>
						}
						{orgFilters.module === '' &&
							<Form.Item
								label={t('filters.module')}
								name='module'
							>
								<Select
									showSearch
									placeholder={t('loadForm.placeholder.module')}
									optionFilterProp='children'
									filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
									options={create_select_options(modules, 'name')}
								/>
							</Form.Item>
						}
						<Form.Item
							label={t('schemasForm.inputs.scope')}
							name='scope'
							rules={[
								{
									required: true,
									message: t('schemasForm.validationMsg.scope'),
								}
							]}
						>
							<Select
								showSearch
								placeholder={t('schemasForm.placeholder.scope')}
								optionFilterProp='children'
								filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
								options={loadScopeOptions}
							/>
						</Form.Item>
					</Fragment>
				}
				<Form.Item
					label={t('routesTable.headers.name')}
					name='name'
					rules={[
						{
							required: true,
							message: t('errorForm.validationMsg.name'),
						}
					]}
				>
					<Input placeholder={t('errorForm.placeholder.name')} />
				</Form.Item>
				<Form.Item
					label={t('loadFileInput.description')}
					name='description'
				>
					<Input placeholder={t('errorForm.placeholder.description')} />
				</Form.Item>
				<Form.Item
					label={t('rulesForm.inputs.active')}
					name='active'
					rules={[
						{
							required: true,
							message: t('schemasForm.validationMsg.boolean'),
						}
					]}
				>
					<Radio.Group>
						<Radio value={true}>{t('schemasForm.booleanOptions.true')}</Radio>
						<Radio value={false}>{t('schemasForm.booleanOptions.false')}</Radio>
					</Radio.Group>
				</Form.Item>
				<Form.Item
					label={t('schemas.title').slice(0, -1)}
					name='schema'
					rules={[
						{
							required: true,
							message: t('rulesForm.validationMsg.schema'),
						}
					]}
				>
					<Select
						showSearch
						placeholder={t('rulesForm.placeholder.schema')}
						optionFilterProp='children'
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
						options={create_select_options(all_self_schemas.schemas, 'name')}
					/>
				</Form.Item>
				<Form.Item
					label={t('rulesForm.inputs.actionType')}
					name='action'
					rules={[
						{
							required: true,
							message: t('rulesForm.validationMsg.action'),
						}
					]}
				>
					<Select
						placeholder={t('rulesForm.placeholder.action')}
						options={[
							{ value: 'FILTER', label: t('statusDesc.ruleActionType.filter') },
							{ value: 'EXCLUDE', label: t('statusDesc.ruleActionType.exclude') },
							{ value: 'INCLUDE', label: t('statusDesc.ruleActionType.include') }
						]}
					/>
				</Form.Item>
				<Form.Item
					label={t('rulesForm.inputs.type')}
					name='type'
					rules={[
						{
							required: true,
							message: t('rulesForm.validationMsg.type'),
						}
					]}
				>
					<Select
						showSearch
						placeholder={t('rulesForm.placeholder.type')}
						optionFilterProp='children'
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
						options={ruleTypesOptions}
					/>
				</Form.Item>
				{(ruleType === 1 || ruleType === 2) &&
					<Form.Item
						label={t('rulesForm.inputs.number')}
						name='number'
						rules={[
							{
								required: true,
								message: t('rulesForm.validationMsg.number'),
							}
						]}
					>
						<InputNumber />
					</Form.Item>
				}
				{ruleType === 3 &&
					<Form.Item
						label={t('rulesForm.inputs.values')}
						name='values'
						extra={t('rulesForm.placeholder.extraValues')}
						rules={[
							{
								required: true,
								message: t('rulesForm.validationMsg.values'),
							}
						]}
					>
						<Input.TextArea placeholder={t('rulesForm.placeholder.values')} autoSize={{ minRows: 2 }} />
					</Form.Item>
				}
				{ruleType === 5 &&
					<Fragment>
						<Form.Item
							label={t('rulesForm.inputs.operator')}
							name='operator'
						>
							<Select
								value={operator}
								onChange={setOperator}
								options={[
									{ value: 'AND', label: t('statusDesc.ruleOperatorType.and') },
									{ value: 'OR', label: t('statusDesc.ruleOperatorType.or') },
									{ value: 'NOT', label: t('statusDesc.ruleOperatorType.not') }
								]}
							/>
						</Form.Item>
						
						<Form.Item label={t('rulesForm.inputs.compoundConditions')}>
							<Card size="small" style={{ backgroundColor: '#fafafa' }}>
								{compoundConditions.map((condition, index) => (
									<Card key={index} size="small" style={{ marginBottom: 16 }}>
										<Space direction="vertical" style={{ width: '100%' }}>
											<Space style={{ width: '100%', justifyContent: 'space-between' }}>
												<span>{t('rulesForm.inputs.condition')} #{index + 1}</span>
												{compoundConditions.length > 1 && (
													<Button
														type="text"
														size="small"
														danger
														icon={<MinusCircleOutlined />}
														onClick={() => removeCondition(index)}
													/>
												)}
											</Space>
											
											<Space style={{ width: '100%' }}>
												<Select
													placeholder={t('rulesForm.inputs.operator')}
													style={{ width: 80 }}
													value={condition.operator}
													onChange={(value) => updateCondition(index, 'operator', value)}
													options={[
														{ value: 'AND', label: t('statusDesc.ruleOperatorType.and') },
														{ value: 'OR', label: t('statusDesc.ruleOperatorType.or') },
														{ value: 'NOT', label: t('statusDesc.ruleOperatorType.not') }
													]}
												/>
												
												<Select
													placeholder={t('schemas.title').slice(0, -1)}
													style={{ width: 150 }}
													value={condition.schema.$oid}
													onChange={(value) => updateCondition(index, 'schema', value)}
													options={create_select_options(all_self_schemas.schemas, 'name')}
												/>
												
												<Input
													placeholder={t('rulesForm.placeholder.extraValues')}
													style={{ flex: 1 }}
													value={condition.values}
													onChange={(e) => {
														updateCondition(index, 'values', e.target.value);
													}}
												/>
											</Space>
										</Space>
									</Card>
								))}
								
								<Button
									type="dashed"
									onClick={addCondition}
									icon={<PlusOutlined />}
									style={{ width: '100%' }}
								>
									{`${t('buttons.add')} ${t('rulesForm.inputs.condition')}`}
								</Button>
							</Card>
						</Form.Item>
					</Fragment>
				}
			</Form>
		</Modal>
	)
}

RulesFormModal.propTypes = {
	messageApi: PropTypes.object.isRequired,
	isModalOpen: PropTypes.bool.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	orgFilters: PropTypes.object.isRequired,
	update: PropTypes.bool.isRequired,
	ruleToUpdate: PropTypes.object.isRequired,
	setRuleToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	skip: PropTypes.number.isRequired
}

export default RulesFormModal;